import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { DataUnavailable } from '~/components/DataUnavailable';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { useReleasesUrlParams } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/hooks/useReleasesUrlParams/useReleasesUrlParams';
import type { ArtistDetails } from '~/modules/artist-roster/types';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { TrackTable } from './TrackTable';

interface TrackListProps {
  uaid: number | null;
  artistData?: ArtistDetails;
  columnSort?: ColumnSort;
  setSortingState?: OnChangeFn<SortingState> | undefined;
}

export function TrackList({
  uaid,
  artistData,
  columnSort,
  setSortingState,
}: TrackListProps) {
  const { pageNumber, filters } = useReleasesUrlParams();
  const { t } = useTranslation('roster');
  const { searchText, releaseType, role } = filters;

  const [isTrackPreReleasesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgTrackPreReleases,
  ]);

  const {
    data: tracksData,
    error,
    isLoading: isLoadingTracks,
    isFetching: isFetchingTracks,
  } = useArtistTracksQuery({
    uaid,
    columnSort,
    pageNumber,
    searchText,
    releaseType,
    isReleased: isTrackPreReleasesEnabled ? true : undefined,
    role,
  });

  if (!artistData) {
    return null;
  }

  if (error) {
    return (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  const trackTableMeta = {
    artistName: artistData.name,
    filtersCount: filters?.releaseType !== undefined ? 1 : 0,
    filtersApplied: filters !== undefined,
    partyId: artistData.partyId,
    uaid,
  };

  return (
    <div className="relative px-xl pb-xl">
      <TrackTable
        data={tracksData?.items ?? []}
        meta={trackTableMeta}
        artistData={artistData}
        isLoadingTracks={isLoadingTracks}
        isFetchingTracks={isFetchingTracks}
        columnSort={columnSort}
        onSortingChange={setSortingState}
      />
    </div>
  );
}

TrackList.displayName = 'TrackList';
